import { useAtom, useAtomValue, useSetAtom } from "jotai";
import { useCallback } from "react";
import {
    selectedMonthAtom,
    documentsQueryAtom,
    refetchDocumentsAtom,
} from "../state/atoms";
import { documentApiService } from "../services/documentApiService";

export function useDocuments() {
    const [selectedMonth, setSelectedMonth] = useAtom(selectedMonthAtom);
    const queryState = useAtomValue(documentsQueryAtom);
    const refetch = useSetAtom(refetchDocumentsAtom);

    const documents = queryState.data?.pages.flatMap(page => page.documents || []) || [];
    const loading = queryState.isPending || queryState.isFetching;
    const error = queryState.error?.message || null;

    const pagination = {
        page: queryState.data?.pages.length || 1,
        pageSize: 20,
        hasMore: queryState.hasNextPage,
        totalCount: queryState.data?.pages[0]?.count || documents.length,
    };

    const refreshDocuments = useCallback(async () => {
        await refetch();
    }, [refetch]);

    const loadNextPage = useCallback(() => {
        if (queryState.hasNextPage && !queryState.isFetchingNextPage) {
            queryState.fetchNextPage();
        }
    }, [queryState]);

    const loadAllPages = useCallback(async () => {
        while (queryState.hasNextPage && !queryState.isFetchingNextPage) {
            await queryState.fetchNextPage();
        }
    }, [queryState]);

    const changeMonth = useCallback((newMonth: string) => {
        setSelectedMonth(newMonth);
    }, [setSelectedMonth]);

    const goToPreviousMonth = useCallback(() => {
        const [year, month] = selectedMonth.split("-").map(Number);
        let newYear = year;
        let newMonth = month - 1;

        if (newMonth < 1) {
            newMonth = 12;
            newYear -= 1;
        }

        const formattedMonth = newMonth.toString().padStart(2, "0");
        changeMonth(`${newYear}-${formattedMonth}`);
    }, [selectedMonth, changeMonth]);

    const goToNextMonth = useCallback(() => {
        const [year, month] = selectedMonth.split("-").map(Number);
        let newYear = year;
        let newMonth = month + 1;

        if (newMonth > 12) {
            newMonth = 1;
            newYear += 1;
        }

        const formattedMonth = newMonth.toString().padStart(2, "0");
        changeMonth(`${newYear}-${formattedMonth}`);
    }, [selectedMonth, changeMonth]);

    const deleteDocument = useCallback(async (documentId: string) => {
        await documentApiService.deleteDocument(documentId);
        await refreshDocuments();
    }, [refreshDocuments]);

    return {
        documents,
        pagination,
        loading: loading || queryState.isFetchingNextPage,
        error,
        refreshDocuments,
        loadNextPage,
        loadAllPages,
        deleteDocument,
        selectedMonth,
        changeMonth,
        goToPreviousMonth,
        goToNextMonth,
    };
}
